<template>
  <BaseCard
    title="Top 10 Industries by Businesses"
    class="h-full"
  >
    <template #content>
         <div class="flex flex-col gap-4 min-h-fit w-full">
        <div class="flex flex-col gap-4 w-full">
          <div v-if="isLoading">
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>
          <div
            v-else-if="top10Industries.length > 0"
            class="flex flex-col gap-4 w-full  rounded-lg"
          >
            <ClientOnly>
              <VChart
                ref="pie"
                class="w-full min-h-[400px] max-h-[500px]"
                :option="chartOption"
                autoresize
              />
              <template #fallback>
                <div class="text-center text-gray-500 p-4">
                  Loading chart...
                </div>
              </template>
            </ClientOnly>
          </div>

          <div
            v-else
            class="flex flex-col items-center justify-center p-12 rounded-lg"
          >
            <Icon
              name="mdi:chart-pie"
              size="48"
              class="text-gray-300 mb-4"
            />
            <p class="text-gray-500 text-center">
              No business data available to display
            </p>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, shallowRef } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import type { ComposeOption } from 'echarts/core'
import type {
  PieSeriesOption,
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
} from 'echarts'
import { useDashboard } from '~/composables/useDashboardCA'

use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
])

type EChartsOption = ComposeOption<
  | PieSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
>

interface IndustryAnalytics {
  sectorName: string
  sectorId: string
  totalBusinesses: number
  totalPrice: number
  averagePrice: number
  averageMultiple: number
}

interface AnalyticsResponse {
  analytics: IndustryAnalytics[]
  summary: unknown
}

const { getIndustryAnalytics } = useDashboard()

const isLoading = ref(true)
const analytics = ref<IndustryAnalytics[]>([])

const top10Industries = computed(() => {
  return [...analytics.value]
    .sort((a, b) => b.totalBusinesses - a.totalBusinesses)
    .slice(0, 10)
})

const generateColors = (count: number) => {
  const palette = [
    '#97BA9F', // greenish
    '#5D8AA8', // blue
    '#F2B134', // gold
    '#F28585', // coral
    '#B77FDD', // purple
    '#FF7498', // pink
    '#57C6E1', // teal
    '#CFE2D5', // pale green
    '#A8C5AE', // light sage
    '#FFA559', // orange
    '#B6E388', // mint
    '#E38690', // blush
    '#AFB4FF', // periwinkle
    '#E27396', // rose
    '#7FD8BE', // mint blue
    '#B9D0BD', // soft teal
  ]

  return Array.from({ length: count }, (_, i) => palette[i % palette.length])
}

const pie = shallowRef<InstanceType<typeof VChart> | null>(null)

let timer: number | undefined
const chartOption = computed<EChartsOption>(() => {
  if (top10Industries.value.length === 0) {
    return {
      title: {
        text: 'No Data',
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#999',
        },
      },
    }
  }

  const data = top10Industries.value.map(item => ({
    value: item.totalBusinesses,
    name: item.sectorName,
  }))

  const colors = generateColors(data.length)

  return {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: '#fff',
      },
      formatter: (params: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { name, value, percent } = params as any
        return `${name}<br/>${value} businesses (${percent}%)`
      },
    },
    legend: {
      orient: 'horizontal',

      textStyle: {
        color: '#666',
      },
    },
    series: [
      {
        name: 'Total Businesses',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: true,
          fontSize: 12,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        labelLine: {
          show: true,
        },
        data,
        color: colors,
      },
    ],
  }
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getIndustryAnalytics()
    const data = response?.data as AnalyticsResponse

    if (data) {
      analytics.value = data.analytics || []
    }
  }
  catch (error) {
    console.error('Error fetching industry analytics chart:', error)
    analytics.value = []
  }
  finally {
    isLoading.value = false
  }
}

onUnmounted(() => {
  stopActions()
})

function getPieSeries(option: EChartsOption | undefined): PieSeriesOption | null {
  if (!option) {
    return null
  }
  const series = option.series
  const firstSeries = Array.isArray(series) ? series[0] : series
  if (!firstSeries || typeof firstSeries !== 'object') {
    return null
  }
  if ((firstSeries as PieSeriesOption).type === 'pie') {
    return firstSeries as PieSeriesOption
  }
  return null
}

function startActions(): void {
  let dataIndex = -1

  const series = getPieSeries(chartOption.value)
  const data = Array.isArray(series?.data) ? series.data : []
  const dataLen = data.length

  if (!pie.value || dataLen === 0) {
    return
  }

  clearInterval(timer)

  timer = window.setInterval(() => {
    const chart = pie.value
    if (!chart) {
      if (timer !== undefined) {
        clearInterval(timer)
        timer = undefined
      }
      return
    }

    chart.dispatchAction({
      type: 'downplay',
      seriesIndex: 0,
      dataIndex,
    })
    dataIndex = (dataIndex + 1) % dataLen
    chart.dispatchAction({
      type: 'highlight',
      seriesIndex: 0,
      dataIndex,
    })
    chart.dispatchAction({
      type: 'showTip',
      seriesIndex: 0,
      dataIndex,
    })
  }, 2000)
}

function stopActions(): void {
  if (timer !== undefined) {
    clearInterval(timer)
    timer = undefined
  }
}

onMounted(async () => {
  await getData()
  await startActions()
})
</script>
